import Splide from '@splidejs/splide';
import { AutoScroll } from '@splidejs/splide-extension-auto-scroll';

/**
 * Partners Logo Carousel component
 * Handles two different carousel variants:
 * 1. Standard carousel with partner-carousel__item elements
 * 2. Gallery grid carousel with jet-engine-gallery-grid elements
 */
export default class PartnersLogoCarousel {
  // Configuration constants
  SELECTORS = {
    carousel: '.partner-carousel',
    container: '.partner-carousel__container',
    slideClass: 'partner-carousel__item',
    wrapperClass: 'elementor-widget-wrap',
    galleryGrid: '.jet-engine-gallery-grid',
    galleryItem: '.jet-engine-gallery-grid__item',
  };

  SPLIDE_CLASSES = {
    container: 'splide',
    track: 'splide__track',
    list: 'splide__list',
    slide: 'splide__slide',
  };

  constructor() {
    this.init();
  }

  /**
   * Initialize the carousel
   */
  init() {
    const carouselElement = document.querySelector(this.SELECTORS.carousel);
    
    if (!carouselElement) return;
    
    // Detect carousel type and initialize appropriate variant
    const hasStandardCarousel = document.querySelectorAll(this.SELECTORS.container).length > 0;
    const hasGalleryGrid = carouselElement.querySelector(this.SELECTORS.galleryGrid);
    
    if (hasStandardCarousel) {
      this.initStandardCarousel();
    }
    
    if (hasGalleryGrid) {
      this.initGalleryGridCarousel();
    }
    
    // Add loaded class after window load
    window.addEventListener('load', () => {
      carouselElement.classList.add('loaded');
    });
  }

  /**
   * Initialize the standard carousel variant
   */
  initStandardCarousel() {
    const splideElements = document.querySelectorAll(this.SELECTORS.container);
    
    splideElements.forEach((splideElement) => {
      // Add Splide classes
      const carouselContainer = document.querySelector(`${this.SELECTORS.carousel}>div`);
      if (carouselContainer) {
        carouselContainer.classList.add(this.SPLIDE_CLASSES.container);
      }
      
      splideElement.classList.add(this.SPLIDE_CLASSES.track);
      
      // Set up list wrapper
      const splideWrapper = splideElement.querySelector(`.${this.SELECTORS.wrapperClass}`);
      if (splideWrapper) {
        splideWrapper.setAttribute('class', this.SPLIDE_CLASSES.list);
      }
      
      // Convert items to slides
      const splideSlides = splideElement.querySelectorAll(`.${this.SELECTORS.slideClass}`);
      splideSlides.forEach((el) => {
        el.setAttribute('class', this.SPLIDE_CLASSES.slide);
      });
      
      // Only initialize Splide if we have enough slides
      if (splideSlides.length > 4) {
        this.createSplideInstance(
          `${this.SELECTORS.carousel}>.elementor-container`,
          this.getCarouselOptions()
        );
      }
    });
  }

  /**
   * Initialize the gallery grid carousel variant
   */
  initGalleryGridCarousel() {
    const carouselElement = document.querySelector(this.SELECTORS.carousel);
    const galleryContainer = carouselElement.querySelector(this.SELECTORS.galleryGrid);
    
    if (!galleryContainer) return;
    
    // Set up Splide structure
    const containerElement = carouselElement.querySelector('.elementor-container');
    if (containerElement) {
      containerElement.classList.add(this.SPLIDE_CLASSES.container);
    }
    
    // Find or create track element
    let trackElement = carouselElement.querySelector(`.${this.SPLIDE_CLASSES.track}`);
    if (!trackElement) {
      trackElement = carouselElement.querySelector(this.SELECTORS.container);
      if (trackElement) {
        trackElement.classList.add(this.SPLIDE_CLASSES.track);
      }
    }
    
    // Get gallery items
    const galleryItems = galleryContainer.querySelectorAll(this.SELECTORS.galleryItem);
    if (galleryItems.length === 0) return;
    
    // Create slide list
    let slideList = carouselElement.querySelector(`.${this.SPLIDE_CLASSES.list}`);
    if (!slideList) {
      slideList = document.createElement('div');
      slideList.className = this.SPLIDE_CLASSES.list;
      galleryContainer.parentNode.appendChild(slideList);
    }
    
    // Convert gallery items to slides
    this.convertGalleryItemsToSlides(galleryItems, slideList);
    
    // Hide original gallery
    galleryContainer.style.display = 'none';
    
    // Initialize Splide with the same options as standard carousel
    this.createSplideInstance(
      `${this.SELECTORS.carousel} .${this.SPLIDE_CLASSES.container}`,
      this.getCarouselOptions()
    );
  }

  /**
   * Convert gallery items to Splide slides
   * @param {NodeList} items - Gallery items to convert
   * @param {HTMLElement} slideList - Slide list container
   */
  convertGalleryItemsToSlides(items, slideList) {
    items.forEach((item) => {
      const slide = document.createElement('div');
      slide.className = this.SPLIDE_CLASSES.slide;
      slide.appendChild(item.cloneNode(true));
      slideList.appendChild(slide);
    });
  }

  /**
   * Create a new Splide instance
   * @param {string} selector - CSS selector for the Splide container
   * @param {Object} options - Splide options
   * @returns {Splide} - Splide instance
   */
  createSplideInstance(selector, options) {
    return new Splide(selector, options).mount({ AutoScroll });
  }

  /**
   * Get options for carousel
   * @returns {Object} - Splide options
   */
  getCarouselOptions() {
    return {
      type: 'loop',
      autoWidth: true,
      perPage: 3,
      interval: 1,
      gap: '70px',
      drag: 'free',
      focus: 'left',
      autoScroll: {
        speed: 1,
      },
      breakpoints: {
        640: {
          perPage: 4,
          speed: 15000,
          gap: '5px',
          pagination: true,
        },
      }
    };
  }
}
